import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import {
  DATE_FORMAT,
  DEFAULT_DISPLAY_VALUE,
  PAGE_NAMES,
  ASSESSMENT_PERIOD_STATUSES,
  MENU_ACTIONS,
  EVALUATION_STATUSES,
  PROJECT_TYPES,
  TIME_TO_DETECT_AUTOMATICALLY_ADDED_PROJECTS
} from '@/constants';
import { Getter } from '@/decorators';
import { ActionMenuItem, AssessmentPeriodDTO, DisplayStatus, ProjectDTO, UserDTO } from '@/models';
import { formatDateToLocalDateTime, formatDateToVTextField } from '@/helpers';
import {
  closeAssessmentPeriod,
  fetchAssessmentPeriodById,
  fetchProjectsInPeriod,
  updateAssessmentPeriod
} from '@/services';
import AssessmentPeriodExport from '@/services/assessment-period-export';
import ActionMenu from '@/components/common/action-menu/ActionMenu.vue';
import AssessmentPeriodProjects from '@/components/pages/assessment-periods/assessment-period-detail/assessment-period-projects/AssessmentPeriodProjects.vue';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import Status from '@/components/common/status/Status.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';

@Component({
  components: {
    ActionMenu,
    AssessmentPeriodProjects,
    ConfirmationDialog,
    Status,
    UserInformation
  }
})
export default class AssessmentPeriodDetail extends Vue {
  @Getter('app/currentUser') user: UserDTO;

  $refs!: {
    formRef: HTMLFormElement;
  };

  private projects: ProjectDTO[] = [];
  private isProjectFetching = true;
  private assessmentPeriodFormData = {} as AssessmentPeriodDTO;
  private assessmentPeriodInfo = {} as AssessmentPeriodDTO;
  private ON_GOING = ASSESSMENT_PERIOD_STATUSES.ON_GOING.label;
  private assessmentPeriodId = '';
  private isEditing = false;
  private isOpenCloseDialog = false;
  private dirtyCheckDialogShown = false;
  private foundActiveProjects = false;
  private ruleOptions = {
    name: [(v: string) => !!v || 'Assessment period name is required'],
    startDate: [
      (v: string) => !!v || 'Start date is required',
      (v: string) => (v && this.validateDateFields()) || 'Start date must be before end date'
    ],
    endDate: [
      (v: string) => !!v || 'End date is required',
      (v: string) => (v && this.validateDateFields()) || 'End date must be after start date'
    ]
  };

  buildAssessmentPeriodFormData(): AssessmentPeriodDTO {
    return {
      ...this.assessmentPeriodInfo,
      startDate: formatDateToVTextField(this.assessmentPeriodInfo.startDate),
      endDate: formatDateToVTextField(this.assessmentPeriodInfo.endDate)
    };
  }

  get displayStatus(): DisplayStatus {
    return ASSESSMENT_PERIOD_STATUSES[this.assessmentPeriodFormData.status];
  }

  get menuItems(): ActionMenuItem[] {
    const menu = [] as ActionMenuItem[];
    if (this.user.isAdmin) {
      menu.push({ label: MENU_ACTIONS.EXPORT, icon: 'mdi-file-table', handler: this.exportAssessmentPeriod });
    }
    if (this.assessmentPeriodFormData.status === this.ON_GOING) {
      menu.push({
        label: MENU_ACTIONS.CLOSE_PERIOD,
        icon: 'mdi-clipboard-check',
        disabled: this.projects.some(project => project.projectStatus !== EVALUATION_STATUSES.DONE.label),
        handler: this.openConfirmDialog
      });
    }
    return menu;
  }

  validateDateFields(): boolean {
    const { startDate, endDate } = this.assessmentPeriodFormData;
    return !startDate || !endDate || moment(startDate).isBefore(moment(endDate));
  }

  formValidate(): boolean {
    return this.$refs.formRef.validate();
  }

  async exportAssessmentPeriod(): Promise<void> {
    const totalDrops = this.getTotalAssessmentDrops();
    const assessmentPeriodExport = new AssessmentPeriodExport(this.assessmentPeriodInfo, this.projects, totalDrops);
    await assessmentPeriodExport.generateXLSXFile();
  }

  async created(): Promise<void> {
    this.assessmentPeriodId = this.$route.params.id;
  }

  async mounted(): Promise<void> {
    await fetchAssessmentPeriodById(this.assessmentPeriodId).then(payload => {
      if (payload.data) {
        this.assessmentPeriodInfo = payload.data;
        this.assessmentPeriodFormData = this.buildAssessmentPeriodFormData();
      }
    });

    await fetchProjectsInPeriod(this.assessmentPeriodId)
      .then(({ data }) => {
        this.projects = data || [];
      })
      .finally(() => {
        this.isProjectFetching = false;
      });

    if (this.$store.getters['app/isAssessmentPeriodJustCreated']) {
      this.foundActiveProjects = this.projects.length > 0;
      this.$store.commit('app/setIsAssessmentPeriodJustCreated', false);
    }
  }

  async onRefetchData(): Promise<void> {
    this.isProjectFetching = true;
    const { data } = await fetchProjectsInPeriod(this.assessmentPeriodId);
    this.projects = data || [];
    this.isProjectFetching = false;
    const currentPeriodCreatedTime = new Date(this.assessmentPeriodInfo.createdAt);
    const existManuallyAddedProject = this.projects.some(project => {
      const currentProjectAssessment = project.projectAssessments.find(
        projectAssessment => projectAssessment.assessmentPeriodId === parseInt(this.assessmentPeriodId)
      );
      const currentPACreatedTime = new Date(currentProjectAssessment?.createdAt || '');
      return (
        Math.abs(currentPACreatedTime.getTime() - currentPeriodCreatedTime.getTime()) >
        TIME_TO_DETECT_AUTOMATICALLY_ADDED_PROJECTS
      );
    });
    if (existManuallyAddedProject) {
      this.foundActiveProjects = false;
    } else {
      this.foundActiveProjects = true;
    }
  }

  editMode(): void {
    this.isEditing = true;
  }

  viewMode(): void {
    if (this.isDirty()) {
      this.dirtyCheckDialogShown = true;
    } else {
      this.isEditing = false;
    }
  }

  isDirty(): boolean {
    const { assessmentPeriodInfo, assessmentPeriodFormData } = this;
    return (
      assessmentPeriodFormData.name !== assessmentPeriodInfo.name ||
      assessmentPeriodFormData.description !== assessmentPeriodInfo.description ||
      this.isDateDirty(assessmentPeriodFormData.startDate, assessmentPeriodInfo.startDate) ||
      this.isDateDirty(assessmentPeriodFormData.endDate, assessmentPeriodInfo.endDate)
    );
  }

  isDateDirty(date: string, originalDate: string): boolean {
    if (!date && !originalDate) {
      return false;
    }
    return moment.utc(date).format(DATE_FORMAT.STORED) !== originalDate;
  }

  onDirtyDialogConfirmed(): void {
    this.dirtyCheckDialogShown = false;
    this.isEditing = false;
    this.assessmentPeriodFormData = this.buildAssessmentPeriodFormData();
  }

  async saveEdit(): Promise<void> {
    if (this.formValidate()) {
      const body = {
        id: this.assessmentPeriodFormData.id,
        name: this.assessmentPeriodFormData.name,
        description: this.assessmentPeriodFormData.description,
        startDate: formatDateToLocalDateTime(this.assessmentPeriodFormData.startDate),
        endDate: formatDateToLocalDateTime(this.assessmentPeriodFormData.endDate),
        createdById: this.assessmentPeriodFormData.createdBy.id
      };
      await updateAssessmentPeriod(body);
      this.isEditing = false;
    }
  }

  async closePeriod(): Promise<void> {
    await closeAssessmentPeriod(this.assessmentPeriodId);
    this.closeConfirmDialog();
    this.$router.go(-1);
  }

  navigateToInitiateEvaluationPage(): void {
    this.$router.push({
      name: PAGE_NAMES.INITIATE_EVALUATION
    });
  }

  displayDate(date: string): string {
    if (date) {
      return moment(date).format(DATE_FORMAT.DISPLAY);
    }
    return DEFAULT_DISPLAY_VALUE.DATE;
  }

  openConfirmDialog(): void {
    this.isOpenCloseDialog = true;
  }

  closeConfirmDialog(): void {
    this.isOpenCloseDialog = false;
  }

  navigateToAssessmentPeriodsList(): void {
    this.$router.push({ name: PAGE_NAMES.ASSESSMENT_PERIODS });
  }

  getTotalAssessmentDrops(): number {
    let result = 0;
    this.projects.forEach(project => {
      let drops = 0;
      const currentProjectAssessment = project.projectAssessments.find(
        projectAssessment => projectAssessment.assessmentPeriodId === parseInt(this.assessmentPeriodId)
      );

      if (project.projectType !== PROJECT_TYPES.INDIVIDUAL_TRACKING) {
        if (currentProjectAssessment?.finalDrops) {
          drops = currentProjectAssessment.finalDrops;
        }
        result = result + drops;
      } else {
        if (currentProjectAssessment) {
          drops = currentProjectAssessment.userEngagements.reduce((prev, userEngagement) => {
            return userEngagement.individualDrops ? prev + userEngagement.individualDrops : prev;
          }, 0);
        }
        result = result + drops;
      }
    });
    return Math.round(result);
  }
}
